// ============================================================================
// useRecibosAcesso — quem pode ver os Recibos da Folha (e com que escopo)
// ============================================================================
// 05/10/2026 — recibos da folha para a gerente. O SERVIDOR decide o acesso
// (/api/folha-recibos?op=acesso): operador da folha → escopo 'rede'; gerente
// liberada (allowlist no servidor) → escopo 'unidades' com as unidades dela;
// o resto → pode=false. Aqui só lemos a resposta pra montar menu/abas.
// 06/10/2026: podePublicar = OPERADOR FINANCEIRO (lista fechada no servidor,
// api/_lib/fin-operadores.js) — o useOperadorFinanceiro usa este hook pra
// decidir menu/rota do Financeiro e da Folha, sem cópia da lista no cliente.
//
// · 1 chamada por SESSÃO: resposta cacheada no sessionStorage por user+token
//   (troca de login = chave nova) e chamadas simultâneas (Sidebar + página)
//   compartilham a mesma promise.
// · Sem flash: enquanto carrega, pode=false — o item de menu só aparece pra
//   quem o servidor liberou.
// · FALHA ≠ "sem acesso" (REGRA LINEAR, revisão 05/10): erro de rede/servidor
//   volta em `erro` (com pode=false, sem cachear o erro) e o hook TENTA DE
//   NOVO sozinho — com espera crescente, ao voltar o foco da aba e ao voltar a
//   rede. Quando qualquer instância consegue, todas recebem (o item do menu
//   aparece sem recarregar a página). `recarregar()` força na hora.
//
// Uso: const { loading, pode, escopo, unitIds, podePublicar, erro, recarregar } = useRecibosAcesso();
//      useRecibosAcesso({ enabled: isGerente })  → não chama a API se false.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { sessionToken } from './secureRest.js';
import { acessoRecibos } from './folhaRecibosApi.js';

const CACHE_KEY = 'fk_recibos_acesso';
const NEGADO = { pode: false, escopo: null, unitIds: null, podePublicar: false };
const emVoo = new Map();      // chave da sessão → Promise do acesso
const ouvintes = new Set();   // (chave, acesso) → avisa as outras instâncias
const RETRY_BASE_MS = 5000;
const RETRY_MAX_MS = 60000;

function chaveSessao(user) {
  const tok = sessionToken();
  if (!user?.id || !tok) return '';
  return `${user.id}|${tok.slice(-16)}`;
}

function lerCache(chave) {
  try {
    const c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (c && c.k === chave && c.v && typeof c.v === 'object') return { ...NEGADO, ...c.v };
  } catch { /* storage bloqueado → busca no servidor */ }
  return null;
}

function gravarCache(chave, v) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ k: chave, v })); } catch { /* best-effort */ }
}

function buscarAcesso(chave) {
  let p = emVoo.get(chave);
  if (!p) {
    p = acessoRecibos()
      .then(v => {
        gravarCache(chave, v);
        for (const fn of ouvintes) { try { fn(chave, v); } catch { /* ouvinte desmontando */ } }
        return v;
      })
      .finally(() => { emVoo.delete(chave); });
    emVoo.set(chave, p);
  }
  return p;
}

const igual = (a, b) => a.loading === b.loading && a.pode === b.pode && a.escopo === b.escopo
  && a.podePublicar === b.podePublicar && a.erro === b.erro
  && JSON.stringify(a.unitIds) === JSON.stringify(b.unitIds);

export function useRecibosAcesso({ enabled = true } = {}) {
  const { user } = useAuth();
  const chave = enabled ? chaveSessao(user) : '';

  const [state, setState] = useState(() => {
    const c = chave ? lerCache(chave) : null;
    return c ? { loading: false, erro: '', ...c } : { loading: !!chave, erro: '', ...NEGADO };
  });
  const [tentativa, setTentativa] = useState(0); // recarregar() / retry
  const [falhas, setFalhas] = useState(0);       // falhas seguidas (agenda o retry)
  const recarregar = useCallback(() => setTentativa(t => t + 1), []);

  // Busca (ou cache)
  useEffect(() => {
    const aplicar = (next) => setState(prev => (igual(prev, next) ? prev : next));
    if (!chave) { aplicar({ loading: false, erro: '', ...NEGADO }); setFalhas(0); return undefined; }
    const c = lerCache(chave);
    if (c) { aplicar({ loading: false, erro: '', ...c }); setFalhas(0); return undefined; }
    let vivo = true;
    // Mantém o erro anterior na tela enquanto tenta de novo (sem piscar)
    setState(prev => {
      const next = { loading: true, ...NEGADO, erro: prev.erro || '' };
      return igual(prev, next) ? prev : next;
    });
    buscarAcesso(chave)
      .then(v => { if (vivo) { aplicar({ loading: false, erro: '', ...NEGADO, ...v }); setFalhas(0); } })
      .catch(err => {
        if (!vivo) return;
        aplicar({ loading: false, ...NEGADO, erro: err?.message || 'Não foi possível verificar o acesso agora.' });
        setFalhas(f => f + 1);
      });
    return () => { vivo = false; };
  }, [chave, tentativa]);

  // Outra instância (Sidebar ↔ página) conseguiu → aplica aqui também
  useEffect(() => {
    if (!chave) return undefined;
    const fn = (k, v) => {
      if (k !== chave) return;
      setState(prev => {
        const next = { loading: false, erro: '', ...NEGADO, ...v };
        return igual(prev, next) ? prev : next;
      });
      setFalhas(0);
    };
    ouvintes.add(fn);
    return () => { ouvintes.delete(fn); };
  }, [chave]);

  // Depois de uma falha: tenta de novo com espera crescente (5s, 10s, 20s… até
  // 1 min), e na hora quando a aba volta a ficar visível ou a rede volta.
  useEffect(() => {
    if (!chave || falhas === 0) return undefined;
    const espera = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (falhas - 1));
    const t = setTimeout(recarregar, espera);
    const onVisivel = () => { if (document.visibilityState === 'visible') recarregar(); };
    document.addEventListener('visibilitychange', onVisivel);
    window.addEventListener('online', recarregar);
    return () => {
      clearTimeout(t);
      document.removeEventListener('visibilitychange', onVisivel);
      window.removeEventListener('online', recarregar);
    };
  }, [chave, falhas, recarregar]);

  return { ...state, recarregar };
}

export default useRecibosAcesso;
